import {
  GetSubscriptionPlansOptions,
  GetSubscriptionPlansResult,
  getSubscriptionPlans,
  GetSubscriptionPlansFields,
} from '../fetchers/subscription_plans';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { assignMswData, useInternalState, serializeQuery } from '../hooks/internalState';
import { useCallback } from 'react';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetSubscriptionPlansOptions<Fields extends GetSubscriptionPlansFields> =
  GetSubscriptionPlansOptions<Fields>;

/**
 * Get all the subscription plans that the user can upgrade to
 * This method returns every subscription plan that the authenticated user can upgrade to.
 */

export function useGetSubscriptionPlans<Fields extends GetSubscriptionPlansFields>(
  getOptions:
    | UseGetSubscriptionPlansOptions<Fields>
    | (() => UseGetSubscriptionPlansOptions<Fields> | null),
  config?: SWRConfiguration<GetSubscriptionPlansResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/subscription_plans${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getSubscriptionPlans({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetSubscriptionPlans, {
    endpoint: '/subscription_plans',
    method: 'GET',
  });
}

/**
 * Get all the subscription plans that the user can upgrade to
 * This method returns every subscription plan that the authenticated user can upgrade to.
 */

export function useGetSubscriptionPlansLazy<Fields extends GetSubscriptionPlansFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetSubscriptionPlansResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetSubscriptionPlansOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/subscription_plans${serializeQuery(options)}`,
          getSubscriptionPlans({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetSubscriptionPlansLazy, {
    endpoint: '/subscription_plans',
    method: 'GET',
  });
}
